import React, { useCallback, useContext } from 'react';
import { useLocation } from 'react-router';

import { useSearchAlerts } from '@/domain/search-alerts/hooks';
import { useSearchAlertsListQuery } from '@/graphql/generated';
import { useLoginStates, useTrackNwtEvent } from '@/hooks';
import { SearchAlertQuery } from '@/types';

import { MAX_ALERT_COUNT } from '../constants';
import { useEditAlertMode, useSearch } from '../hooks';
import { useSearchQuery } from './search-query-context';

type ToggleAlertOptions = {
  email?: string;
  propTrackActionData?: Record<string, string>;
};

type SearchAlertsValue = {
  toggleAlert: (options?: ToggleAlertOptions) => void;
  isAlertActive: boolean;
  areAlertsInitialized: boolean;
  alertQuery: SearchAlertQuery;
  isMaximumReached: boolean;
  alertId: string | undefined;
};

const SearchAlertsContext = React.createContext<SearchAlertsValue | undefined>(undefined);

export const useSearchAlertsContext = (): SearchAlertsValue => {
  const context = useContext(SearchAlertsContext);
  if (!context) {
    throw new Error('useSearchAlertsContext must be used within a SearchAlertsProvider');
  }

  return context;
};

export const SearchAlertsProvider: React.FC = ({ children }) => {
  const { data: searchData } = useSearch();
  const { alertQuery } = useSearchQuery();
  const { isLoggedIn } = useLoginStates();
  const { previousAlertId, quitEditMode } = useEditAlertMode();
  const trackNwtEvent = useTrackNwtEvent();
  const location = useLocation();

  const { data: searchAlertsData } = useSearchAlertsListQuery({ skip: !isLoggedIn });
  const { createSearchAlert, deleteSearchAlert } = useSearchAlerts();

  const queryId = searchData?.jobSearchByQuery?.searchQuery.guid;

  let alertId: string | undefined;
  let isMaximumReached = false;
  let areAlertsInitialized = false;
  if (searchAlertsData?.viewer?.searchAlerts?.__typename === 'SearchAlertResultResponse') {
    alertId = searchAlertsData.viewer.searchAlerts.collection?.find((element) => element?.queryId === queryId)?.id;
    isMaximumReached = (searchAlertsData.viewer.searchAlerts.collection?.length ?? 0) >= MAX_ALERT_COUNT;
    areAlertsInitialized = !!queryId && !!searchAlertsData.viewer.searchAlerts.collection;
  }

  const toggleAlert: SearchAlertsValue['toggleAlert'] = useCallback(
    ({ email, propTrackActionData } = {}) => {
      if (alertId) {
        deleteSearchAlert({
          alertId,
          propTrackActionData: propTrackActionData
            ? { PropTrackAction: 'jobs_delete_search_alert_completed', ...propTrackActionData }
            : undefined,
        });
        if (previousAlertId) {
          quitEditMode();
        }
      } else {
        if (previousAlertId) {
          const params = new URLSearchParams(location.search);
          params.delete('edit_alert');

          trackNwtEvent({
            event: 'clicked',
            page: 'jobs_search/serp',
            element: 'jobs_search_alert_edit_confirm',
            query: params.toString(),
          });
        }

        const propTrackAction = propTrackActionData
          ? {
              PropTrackAction: previousAlertId ? 'jobs_search_alert_edit_confirm' : 'jobs_search_alert_create_confirm',
              ...propTrackActionData,
            }
          : undefined;

        createSearchAlert({
          alertQuery,
          email,
          queryId,
          propTrackActionData: propTrackAction,
          onCompleted: (data) => {
            if (previousAlertId && data.createSearchAlert?.__typename === 'SearchAlertCreationSuccess') {
              deleteSearchAlert({ alertId: previousAlertId, showError: false });
              quitEditMode();
            }
          },
        });
      }
    },
    [
      alertId,
      alertQuery,
      createSearchAlert,
      deleteSearchAlert,
      location.search,
      previousAlertId,
      queryId,
      quitEditMode,
      trackNwtEvent,
    ]
  );

  const value: SearchAlertsValue = {
    alertId,
    alertQuery,
    areAlertsInitialized: isLoggedIn ? areAlertsInitialized : true,
    isAlertActive: isLoggedIn ? !!alertId : false,
    isMaximumReached,
    toggleAlert,
  };

  return <SearchAlertsContext.Provider value={value}>{children}</SearchAlertsContext.Provider>;
};
